import { Icon } from '@iconify/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createScratchBrushSet,
  estimateScratchProgress,
  eraseScratchAtPoint,
  eraseScratchStroke,
  getPointerPosition,
  getScratchBrushRadius,
  hasReachedScratchRevealThreshold,
  loadScratchImage,
  paintScratchCover,
  paintScratchResult,
  SCRATCH_AUTO_REVEAL_DELAY_MS,
  setupScratchCanvas,
  type ImageDisplayRect,
  type ScratchBrushSet,
  type ScratchPoint,
} from '@/lib/raspadinha-scratch'
import styles from './RaspadinhaScratchCard.module.css'

type RaspadinhaScratchCardProps = {
  scratchImageUrl: string
  resultImageUrl: string
  canInteractAfterReveal?: boolean
  onBeforeFirstScratch?: () => Promise<unknown>
  onRevealedInteract?: () => void
  onRevealComplete?: () => void
}

type ScratchCardStatus = 'loading' | 'ready' | 'error'

export function RaspadinhaScratchCard({
  scratchImageUrl,
  resultImageUrl,
  canInteractAfterReveal = false,
  onBeforeFirstScratch,
  onRevealedInteract,
  onRevealComplete,
}: RaspadinhaScratchCardProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const resultCanvasRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const contextRef = useRef<CanvasRenderingContext2D | null>(null)
  const dimensionsRef = useRef({ width: 0, height: 0 })
  const imageFrameRef = useRef<ImageDisplayRect | null>(null)
  const sourcesRef = useRef({ scratch: '', result: '' })
  const brushSetRef = useRef<ScratchBrushSet | null>(null)
  const scratchSeedRef = useRef(0)
  const lastPointRef = useRef<ScratchPoint | null>(null)
  const isScratchingRef = useRef(false)
  const isRevealedRef = useRef(false)
  const autoRevealScheduledRef = useRef(false)
  const autoRevealTimeoutRef = useRef<number | null>(null)
  const progressCheckFrameRef = useRef<number | null>(null)
  const firstScratchPreparedRef = useRef(false)

  const [status, setStatus] = useState<ScratchCardStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRevealed, setIsRevealed] = useState(false)
  const [isPreparingOutcome, setIsPreparingOutcome] = useState(false)

  const clearAutoRevealTimeout = useCallback(() => {
    if (autoRevealTimeoutRef.current !== null) {
      window.clearTimeout(autoRevealTimeoutRef.current)
      autoRevealTimeoutRef.current = null
    }
  }, [])

  const completeReveal = useCallback(() => {
    if (isRevealedRef.current) {
      return
    }

    clearAutoRevealTimeout()
    isRevealedRef.current = true
    setIsRevealed(true)
    onRevealComplete?.()
  }, [clearAutoRevealTimeout, onRevealComplete])

  const scheduleAutoReveal = useCallback(() => {
    if (autoRevealScheduledRef.current || isRevealedRef.current) {
      return
    }

    autoRevealScheduledRef.current = true
    isScratchingRef.current = false
    lastPointRef.current = null

    autoRevealTimeoutRef.current = window.setTimeout(() => {
      autoRevealTimeoutRef.current = null
      completeReveal()
    }, SCRATCH_AUTO_REVEAL_DELAY_MS)
  }, [completeReveal])

  const checkScratchProgress = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || isRevealedRef.current || autoRevealScheduledRef.current) {
      return
    }

    const progress = estimateScratchProgress(canvas)
    if (hasReachedScratchRevealThreshold(progress)) {
      scheduleAutoReveal()
    }
  }, [scheduleAutoReveal])

  const scheduleProgressCheck = useCallback(() => {
    if (progressCheckFrameRef.current !== null) {
      return
    }

    progressCheckFrameRef.current = window.requestAnimationFrame(() => {
      progressCheckFrameRef.current = null
      checkScratchProgress()
    })
  }, [checkScratchProgress])

  const initializeCanvas = useCallback(async () => {
    const container = containerRef.current
    const resultCanvas = resultCanvasRef.current
    const canvas = canvasRef.current

    if (!container || !resultCanvas || !canvas) {
      return
    }

    const width = container.clientWidth
    const height = container.clientHeight

    if (width <= 0 || height <= 0) {
      return
    }

    const hasSameDimensions =
      width === dimensionsRef.current.width && height === dimensionsRef.current.height

    const hasSameSources =
      sourcesRef.current.scratch === scratchImageUrl &&
      sourcesRef.current.result === resultImageUrl

    if (hasSameDimensions && hasSameSources && contextRef.current && imageFrameRef.current) {
      return
    }

    setStatus('loading')
    setErrorMessage(null)
    isRevealedRef.current = false
    autoRevealScheduledRef.current = false
    clearAutoRevealTimeout()
    setIsRevealed(false)
    lastPointRef.current = null
    isScratchingRef.current = false

    try {
      const [coverImage, outcomeImage] = await Promise.all([
        loadScratchImage(scratchImageUrl),
        loadScratchImage(resultImageUrl),
      ])

      const liveWidth = container.clientWidth
      const liveHeight = container.clientHeight

      if (liveWidth <= 0 || liveHeight <= 0) {
        throw new Error('Área de raspagem indisponível.')
      }

      const backgroundColor =
        getComputedStyle(container).backgroundColor || 'rgb(23, 23, 23)'

      const resultContext = setupScratchCanvas(resultCanvas, liveWidth, liveHeight)
      const scratchContext = setupScratchCanvas(canvas, liveWidth, liveHeight)

      const frame = paintScratchCover(scratchContext, coverImage, liveWidth, liveHeight, {
        backgroundColor,
        anchor: 'top',
      })

      paintScratchResult(resultContext, outcomeImage, liveWidth, liveHeight, frame, backgroundColor)

      contextRef.current = scratchContext
      dimensionsRef.current = { width: liveWidth, height: liveHeight }
      imageFrameRef.current = frame
      sourcesRef.current = { scratch: scratchImageUrl, result: resultImageUrl }
      brushSetRef.current = createScratchBrushSet(getScratchBrushRadius(frame.drawWidth))
      scratchSeedRef.current = 0
      setStatus('ready')
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Não foi possível preparar a raspadinha. Tente novamente.'
      setErrorMessage(message)
      setStatus('error')
    }
  }, [clearAutoRevealTimeout, resultImageUrl, scratchImageUrl])

  useEffect(() => {
    void initializeCanvas()
  }, [initializeCanvas])

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) {
        return
      }

      const { width, height } = entry.contentRect
      const roundedWidth = Math.round(width)
      const roundedHeight = Math.round(height)

      if (
        roundedWidth === dimensionsRef.current.width &&
        roundedHeight === dimensionsRef.current.height
      ) {
        return
      }

      if (!isRevealedRef.current) {
        void initializeCanvas()
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [initializeCanvas])

  useEffect(() => {
    return () => {
      clearAutoRevealTimeout()
      if (progressCheckFrameRef.current !== null) {
        window.cancelAnimationFrame(progressCheckFrameRef.current)
      }
    }
  }, [clearAutoRevealTimeout])

  const handlePointerDown = useCallback(
    async (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (status !== 'ready' || isRevealedRef.current || autoRevealScheduledRef.current || isPreparingOutcome) {
        return
      }

      const canvas = canvasRef.current
      const context = contextRef.current
      const brushSet = brushSetRef.current
      if (!canvas || !context || !brushSet) {
        return
      }

      if (onBeforeFirstScratch && !firstScratchPreparedRef.current) {
        firstScratchPreparedRef.current = true
        setIsPreparingOutcome(true)

        try {
          await onBeforeFirstScratch()
          await initializeCanvas()
        } catch (error) {
          firstScratchPreparedRef.current = false
          const message =
            error instanceof Error
              ? error.message
              : 'Não foi possível registrar sua raspadinha.'
          setErrorMessage(message)
          setStatus('error')
          return
        } finally {
          setIsPreparingOutcome(false)
        }
      }

      event.preventDefault()
      canvas.setPointerCapture(event.pointerId)
      isScratchingRef.current = true

      const point = getPointerPosition(canvas, event.clientX, event.clientY)
      lastPointRef.current = point
      scratchSeedRef.current += 1
      eraseScratchAtPoint(context, point, brushSet, scratchSeedRef.current)
      scheduleProgressCheck()
    },
    [initializeCanvas, isPreparingOutcome, onBeforeFirstScratch, scheduleProgressCheck, status]
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isScratchingRef.current || isRevealedRef.current || autoRevealScheduledRef.current) {
        return
      }

      const canvas = canvasRef.current
      const context = contextRef.current
      const brushSet = brushSetRef.current
      const lastPoint = lastPointRef.current
      if (!canvas || !context || !brushSet || !lastPoint) {
        return
      }

      event.preventDefault()
      const point = getPointerPosition(canvas, event.clientX, event.clientY)
      scratchSeedRef.current += 1
      eraseScratchStroke(context, lastPoint, point, brushSet, scratchSeedRef.current)
      lastPointRef.current = point
      scheduleProgressCheck()
    },
    [scheduleProgressCheck]
  )

  const stopScratching = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (canvasRef.current?.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId)
    }

    isScratchingRef.current = false
    lastPointRef.current = null
  }, [])

  const isInteractableAfterReveal = isRevealed && canInteractAfterReveal

  const handleRevealedInteract = useCallback(() => {
    if (!isInteractableAfterReveal) {
      return
    }

    onRevealedInteract?.()
  }, [isInteractableAfterReveal, onRevealedInteract])

  const handleRevealedKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isInteractableAfterReveal) {
        return
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onRevealedInteract?.()
      }
    },
    [isInteractableAfterReveal, onRevealedInteract]
  )

  return (
    <div className={styles.card}>
      <div
        ref={containerRef}
        className={`${styles.scratchArea} ${isInteractableAfterReveal ? styles.scratchAreaInteractive : ''}`}
        role={isInteractableAfterReveal ? 'button' : undefined}
        tabIndex={isInteractableAfterReveal ? 0 : undefined}
        aria-label={isInteractableAfterReveal ? 'Toque para ver seu prêmio contemplado' : undefined}
        onClick={isInteractableAfterReveal ? handleRevealedInteract : undefined}
        onKeyDown={isInteractableAfterReveal ? handleRevealedKeyDown : undefined}
      >
        <canvas
          ref={resultCanvasRef}
          className={styles.resultCanvas}
          aria-hidden
        />

        <canvas
          ref={canvasRef}
          className={`${styles.scratchCanvas} ${isRevealed ? styles.scratchCanvasRevealed : ''}`}
          aria-label="Raspe para revelar o resultado"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopScratching}
          onPointerCancel={stopScratching}
          onPointerLeave={stopScratching}
        />

        {status === 'loading' || isPreparingOutcome ? (
          <div className={styles.overlayState} role="status" aria-live="polite">
            <Icon className={styles.overlayIcon} icon="mdi:loading" aria-hidden />
            <span>{isPreparingOutcome ? 'Registrando sua raspadinha...' : 'Preparando raspadinha...'}</span>
          </div>
        ) : null}

        {status === 'error' ? (
          <div className={styles.overlayState} role="alert">
            <Icon className={styles.overlayIconError} icon="mdi:alert-circle-outline" aria-hidden />
            <span>{errorMessage ?? 'Não foi possível carregar a raspadinha.'}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
